import React from "react";
import { cn } from "@/lib/utils";

/**
 * Impo brand system — OFFICIAL vector wordmark.
 * Paths sourced directly from /public/impo-wordmark.svg (the customer-supplied logo).
 *
 * Variations:
 *   <Logo />              → full wordmark  "▰ impo"
 *   <Logo mark />         → square app-icon variant (wordmark centred in 1:1 canvas)
 *   <Logo dot />          → just the blue parallelogram accent
 *
 * Theme behaviour:
 *   - Letter strokes use `currentColor` so they inherit foreground colour
 *     (deep navy in light mode, ivory in dark mode).
 *   - The parallelogram accent uses `hsl(var(--flag))` = the exact brand
 *     electric blue (#0759fd).
 */

// Vector paths lifted verbatim from the official SVG (viewBox 1590.56 × 751.19)
const WORDMARK_VIEWBOX = "0 0 1590.56 751.19";
const LETTER_PATHS = [
  // 'm'
  "M559,192.19h0c-51.26,0-96.84,24.24-125.94,61.88-29.1-37.64-74.69-61.88-125.94-61.88h0c-87.87,0-159.11,71.24-159.11,159.11v226.67h65.93v-227.41c0-51.14,41.46-92.59,92.59-92.59h0c51.14,0,92.59,41.46,92.59,92.59v227.41h67.11v-226.67c0-2.45-.06-4.88-.17-7.3,3.36-48.08,43.43-86.03,92.36-86.03h0c51.14,0,92.59,41.46,92.59,92.59v227.41h67.11v-226.67c0-87.87-71.24-159.11-159.11-159.11Z",
  // 'o'
  "M1399.91,192.19c-105.29,0-190.65,86.36-190.65,192.89s85.36,192.89,190.65,192.89,190.65-86.36,190.65-192.89-85.36-192.89-190.65-192.89ZM1399.91,519c-73.11,0-132.37-59.96-132.37-133.93s59.26-133.93,132.37-133.93,132.37,59.96,132.37,133.93-59.26,133.93-132.37,133.93Z",
  // 'p'
  "M968.76,192.19c-104.5,0-189.34,85.06-190.62,190.48h-.03v368.52h67.11v-219.21c33.27,28.67,76.4,45.99,123.54,45.99,105.29,0,190.65-86.36,190.65-192.89s-85.36-192.89-190.65-192.89ZM968.76,519c-73.11,0-132.37-59.96-132.37-133.93s59.26-133.93,132.37-133.93,132.37,59.96,132.37,133.93-59.26,133.93-132.37,133.93Z",
  // 'i' stem
  "M0,178.11v399.85h75.96V135.11L0,178.11Z",
];
const DOT_PARALLELOGRAM = "M75.96,0L0,43v85.96l75.96-47V0Z";

function WordmarkSvg({ size = 32, title = "impo", accentColor = "hsl(var(--flag))" }) {
  const H = 751.19;
  const W = 1590.56;
  const height = size;
  const width = (W / H) * size;
  return (
    <svg
      viewBox={WORDMARK_VIEWBOX}
      width={width}
      height={height}
      role="img"
      aria-label={title}
      fill="currentColor"
      style={{ display: "block" }}
    >
      {LETTER_PATHS.map((d, i) => (
        <path key={i} d={d} fill="currentColor" fillRule="evenodd" />
      ))}
      <path d={DOT_PARALLELOGRAM} fill={accentColor} />
    </svg>
  );
}

function AppIconSvg({ size = 128, title = "impo" }) {
  // The customer artwork centres the wordmark inside a 1024×1024 square with ~15% padding.
  // We reproduce that composition using the same paths so it stays sharp at any raster size.
  const CANVAS = 1024;
  const wordmarkTargetW = 800; // matches the customer PNG proportions
  const wordmarkScale = wordmarkTargetW / 1590.56;
  const wordmarkH = 751.19 * wordmarkScale;
  const offsetX = (CANVAS - wordmarkTargetW) / 2;
  const offsetY = (CANVAS - wordmarkH) / 2;
  return (
    <svg
      viewBox={`0 0 ${CANVAS} ${CANVAS}`}
      width={size}
      height={size}
      role="img"
      aria-label={title}
      style={{ display: "block" }}
    >
      <g transform={`translate(${offsetX} ${offsetY}) scale(${wordmarkScale})`}>
        {LETTER_PATHS.map((d, i) => (
          <path key={i} d={d} fill="currentColor" fillRule="evenodd" />
        ))}
        <path d={DOT_PARALLELOGRAM} fill="hsl(var(--flag))" />
      </g>
    </svg>
  );
}

function DotSvg({ size = 32, title = "impo mark" }) {
  return (
    <svg
      viewBox="0 0 75.96 128.96"
      width={size * (75.96 / 128.96)}
      height={size}
      role="img"
      aria-label={title}
      style={{ display: "block" }}
    >
      <path d={DOT_PARALLELOGRAM} fill="hsl(var(--flag))" />
    </svg>
  );
}

/**
 * Default export — full wordmark by default.
 * Pass `mark` for the square app-icon composition.
 * Pass `dot` for just the parallelogram accent.
 */
export default function Logo({
  size = 32,
  className,
  mark = false,
  dot = false,
  variant, // "onDark" — forces white letters + a light-blue accent for use on the primary-blue TopBar
}) {
  const El = dot ? DotSvg : mark ? AppIconSvg : WordmarkSvg;
  const onDark = variant === "onDark";
  return (
    <span
      className={cn(
        "impo-wordmark inline-flex items-center select-none leading-none",
        onDark ? "text-white" : "text-foreground",
        className,
      )}
      data-testid={dot ? "impo-dot" : mark ? "impo-app-icon" : "impo-wordmark"}
      aria-label="impo"
      style={{ height: `${size}px` }}
    >
      {onDark && !dot && !mark ? (
        <WordmarkSvg size={size} accentColor="#ffffff" />
      ) : (
        <El size={size} />
      )}
    </span>
  );
}

// Named export for the dot-only variant (for tests and explicit usage)
export function LogoMark({ size = 32, className }) {
  return (
    <span
      className={cn("impo-mark inline-flex items-center select-none leading-none", className)}
      data-testid="impo-dot"
      aria-label="impo mark"
      style={{ height: `${size}px` }}
    >
      <DotSvg size={size} />
    </span>
  );
}
